/**
 * Top-level router `errorElement`. Renders a minimal fallback for unexpected
 * render/loader errors so a thrown error doesn't blank the whole app.
 */
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { ApiError, statusToCode } from '@/api/errors';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export function RouteError() {
  const { t } = useCopy();
  useDocumentTitle(t('routeError.title', 'Unexpected error'));
  const error = useRouteError();
  let message = t('routeError.generic', 'Something went wrong.');
  let described: unknown = error;
  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText}`;
    // Carry the response status into the copyable details.
    described = new ApiError(statusToCode(error.status), error.status, {
      details: error.statusText ? { message: error.statusText } : {},
    });
  } else if (error instanceof Error) {
    message = error.message;
  }
  return (
    <main style={{ padding: '2rem', maxWidth: '32rem', margin: '0 auto' }}>
      <h1>{t('routeError.title', 'Unexpected error')}</h1>
      <p>{message}</p>
      <ErrorDetails
        error={described}
        headline={t('routeError.title', 'Unexpected error')}
        intent="read"
      />
      <a href="/">{t('routeError.home', 'Return home')}</a>
    </main>
  );
}
